import { motion, useReducedMotion } from 'framer-motion';
import Link from 'next/link';
import { fraunces } from './fonts';

// Shared design tokens — warm paper + deep ink, teal brand anchor.
export const INK = 'tw-text-[#1D2D28] dark:tw-text-[#EBE6D9]';
export const MUTED = 'tw-text-[#5F6F69] dark:tw-text-[#93A69E]';
export const PAPER_CARD =
  'tw-bg-white dark:tw-bg-[#15231E] tw-border tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F]';

export function Eyebrow({ children, className = '' }) {
  return (
    <p
      className={`tw-font-mono tw-text-[11px] sm:tw-text-xs tw-font-medium tw-uppercase tw-tracking-[0.28em] tw-text-primary-700 dark:tw-text-primary-300 tw-mb-4 ${className}`}
    >
      {children}
    </p>
  );
}

export function DisplayHeading({
  as: Tag = 'h2',
  children,
  className = '',
  ...rest
}) {
  return (
    <Tag
      className={`${fraunces.className} tw-font-semibold tw-tracking-tight ${INK} tw-m-0 ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

// Italic teal accent inside display headings.
export function Accent({ children }) {
  return (
    <em className="tw-italic tw-font-medium tw-text-primary-700 dark:tw-text-primary-300">
      {children}
    </em>
  );
}

export function Reveal({ children, delay = 0, className = '' }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.65, delay, ease: [0.21, 0.65, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function CtaButton({ onClick, href, children, className = '', light }) {
  const base = `tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-cursor-pointer tw-px-7 tw-py-3.5 tw-text-base tw-font-semibold tw-no-underline tw-transition-all tw-duration-200 hover:-tw-translate-y-0.5 ${
    light
      ? 'tw-bg-[#FAF8F1] tw-text-primary-800 hover:tw-bg-white hover:tw-text-primary-900 tw-shadow-[0_10px_30px_-10px_rgba(0,0,0,0.45)]'
      : 'tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 hover:tw-text-white tw-shadow-[0_10px_30px_-10px_rgba(0,137,123,0.7)]'
  } ${className}`;
  if (href) {
    // Internal paths get client-side navigation; hash anchors and external
    // URLs keep a plain <a>.
    if (href.startsWith('/'))
      return (
        <Link href={href} className={base}>
          {children}
        </Link>
      );
    return (
      <a href={href} className={base}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={base}>
      {children}
    </button>
  );
}

export function GhostButton({ onClick, href, children, className = '' }) {
  const base = `tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-transparent tw-cursor-pointer tw-px-7 tw-py-3.5 tw-text-base tw-font-semibold tw-no-underline tw-border tw-border-solid tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] ${INK} hover:tw-border-primary-600 hover:tw-text-primary-700 dark:hover:tw-text-primary-300 tw-transition-colors ${className}`;
  if (href) {
    // Internal paths get client-side navigation; hash anchors and external
    // URLs keep a plain <a>.
    if (href.startsWith('/'))
      return (
        <Link href={href} className={base}>
          {children}
        </Link>
      );
    return (
      <a href={href} className={base}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={base}>
      {children}
    </button>
  );
}

export function ArrowIcon({ className = 'tw-w-4 tw-h-4' }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 8h13M9 3l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PlaneIcon({ className = 'tw-w-5 tw-h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"
      />
    </svg>
  );
}

// The square TF palm mark (same path as public/logo.svg) inlined so navs can
// ink it via currentColor — the file assets only exist in white and teal.
export function TfMark({ className = 'tw-w-auto tw-h-7' }) {
  return (
    <svg
      className={className}
      viewBox="436.33 555.19 935.39 698.57"
      aria-hidden="true"
    >
      <path
        fill="currentColor"
        d="M1161 726.13v118.48h210.76l-72.86 160.27h-137.89v248.88l-203.42-.67a7.8 7.8 0 0 1-7.77-7.35l-23.46-426.29 164.38 111.98c-25.71-132.56-143.8-139.8-143.8-139.8l175.41-9.19c-69.1-82.74-189.87-25.34-189.87-25.34l107.03-99.12c-102.83-31.33-135.13 66.33-135.13 66.33-36.95-80.32-125.34-72.45-128.56-73.25-3.2-.8 83.01 98.08 84.34 99.6-120.78-55.67-174.31 36.98-174.31 36.98H865C716.38 803.7 717.18 939.46 717.18 939.46s164.11-118.49 165.42-120c-.7 2.93-14.75 282.92-24.19 425.48-.29 4.41-3.96 7.8-8.37 7.79l-202.91-.67V724.43H483.57l-47.2-169.24 935.39 1.49v169.46H1161z"
      />
    </svg>
  );
}

// Round passport stamp with circular text and a plane in the middle.
export function Stamp({ className = '', text = 'TRAVELFEED · EST. 2018 · ' }) {
  return (
    <svg
      viewBox="0 0 120 120"
      className={className}
      aria-hidden="true"
      role="presentation"
    >
      <defs>
        <path
          id="tf-stamp-circle"
          d="M60,60 m-40,0 a40,40 0 1,1 80,0 a40,40 0 1,1 -80,0"
        />
      </defs>
      <circle
        cx="60"
        cy="60"
        r="56"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeDasharray="2.5 4"
      />
      <circle
        cx="60"
        cy="60"
        r="48"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <text
        fontSize="10.5"
        letterSpacing="2.6"
        fill="currentColor"
        fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
        fontWeight="600"
      >
        <textPath href="#tf-stamp-circle">{text}</textPath>
      </text>
      <path
        fill="currentColor"
        transform="translate(48 48)"
        d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"
      />
    </svg>
  );
}

// Check bullet used in feature lists.
export function CheckItem({ children }) {
  return (
    <li className="tw-flex tw-items-start tw-gap-3">
      <span className="tw-mt-0.5 tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-600/10 dark:tw-bg-primary-300/20">
        <svg
          className="tw-h-3 tw-w-3 tw-text-primary-700 dark:tw-text-primary-300"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </span>
      <span className={`${MUTED} tw-text-[15px] tw-leading-relaxed`}>
        {children}
      </span>
    </li>
  );
}
